/** What the agent is told about views, generated from the catalog so the two can't disagree. */
import { COMPONENTS, type ComponentDef } from './spec/catalog.ts'
import { FORMAT_DOCS } from './spec/format.ts'
import { ICON_NAMES } from './spec/icons.ts'
import { typeLabel, type Field } from './spec/schema.ts'

function propLine(name: string, field: Field) {
  const need = field.optional
    ? 'optional'
    : field.default !== undefined
      ? `default ${JSON.stringify(field.default)}`
      : 'required'
  return `  - ${name} (${typeLabel(field)}, ${need}): ${field.doc}`
}

const componentEntry = (c: ComponentDef) =>
  [
    `### ${c.type}`,
    `${c.summary} ${c.use}`,
    ...Object.entries(c.props).map(([name, field]) => propLine(name, field)),
    `Example: ${JSON.stringify(c.example)}`,
  ].join('\n')

/** The rule: Softmax's components first, with your data in them, and your own only for what they can't show. */
export function viewGuide() {
  return `## Components
Show things with Softmax's components, in this order:
1. Find the component that shows it and put your data in its props. Most of what you show is one component with your values in it.
2. When no single component fits, combine them: a frame around a chart and a table, a grid of metrics, a repeat over rows, tabs between views.
3. Only when nothing in the catalog, alone or combined, can show it (a game board, a heatmap, a bespoke diagram), build that one piece as a custom component, and say why in its reason.

A custom component is your own code: it doesn't get the improvements Softmax's components do, and it can't take clicks. Never rebuild a chart, table or card as custom.

A view is {"version":1,"title":"optional","body":[nodes]}. A node is {"type":"<component>", ...props}. Write the values in yourself: a view reads nothing. A view that doesn't pass its check isn't shown, and the next message's client data says what to fix under block_problems. To offer choices, use button components with an ask action, so the user can answer with one click. An action is {"ask":"what the user says"} or {"open":"/a-path-on-this-site or an https:// link"}. Show a view when it is clearer than a sentence, never just to decorate. Images from other sites are refused.

Formats for a format prop: ${Object.entries(FORMAT_DOCS)
    .map(([name, doc]) => `${name}: ${doc}`)
    .join(' ')}
Icons for an icon prop: ${ICON_NAMES.join(', ')}

${COMPONENTS.map(componentEntry).join('\n\n')}`
}
